/**
 * Marketing Tour — Marketing's walk-through.
 * W66.X16 — in-platform guided tour.
 *
 * Role: Marketing (Marketing)
 * Tag color: Yellow (#EAB308)
 */

import type { GuidedTourStep } from "./types";

export const MARKETING_TOUR_STEPS: GuidedTourStep[] = [
  {
    stepNumber: 1,
    selector: '[data-tour="funnel-page-header"]',
    title: "Step 1 — The Marketing funnel page",
    narration:
      "Marketing lands on the Funnel page — the Marketing view of the pipeline. This is not a marketing-only silo. The same data that the AE uses to manage his deals feeds this page. Marketing can see, in real time, where the leads she generated are sitting in the pipeline. If marketing is generating leads that die in the BDR queue, Marketing sees it here. That visibility is the accountability mechanism.",
    action: "read",
    route: "/#/funnel",
  },
  {
    stepNumber: 2,
    selector: '[data-tour="funnel-trapezoid"]',
    title: "Step 2 — The conversion funnel",
    narration:
      "The conversion funnel shows the continuous trapezoid: the shape of the pipeline from raw lead to closed deal. Each stage narrows — Leads, MQLs, SALs, Opportunities, Closed Won. The width of each band is proportional to the actual volume at that stage. A wide top and narrow bottom means the funnel is leaky somewhere in the middle. Marketing reads this shape to understand where the system is working and where it is breaking down.",
    action: "read",
    route: "/#/funnel",
  },
  {
    stepNumber: 3,
    selector: '[data-tour="funnel-waterfall"]',
    title: "Step 3 — MQL-to-SAL conversion waterfall",
    narration:
      "The MQL-to-SAL waterfall shows the math: of all the leads marketing declared as Marketing Qualified, how many did the BDR team accept as Sales Accepted? How many were recycled? How many were rejected with a reason code? This is the marketing-honesty check. Marketing cannot claim a lead is qualified if the BDR who worked it rejected it. The waterfall shows that conversion rate by source, by campaign, by time period. If Marketing's LinkedIn campaign has a 40% reject rate, that is a quality problem, not a volume problem.",
    coachingBeat:
      "Marketing-honesty check: MQL-to-SAL conversion rate this quarter: 58%. Industry benchmark for this segment: 65-75%. The gap is concentrated in two sources: paid search (44% conversion) and event leads (38% conversion). Organic and referral are at 79%. The data suggests a quality filter problem, not a volume problem.",
    action: "read",
    route: "/#/funnel",
  },
  {
    stepNumber: 4,
    selector: '[data-tour="funnel-channel-mix"]',
    title: "Step 4 — Channel mix",
    narration:
      "The channel mix shows where qualified pipeline is actually coming from: organic, paid, events, referrals, outbound. Not impressions. Not clicks. Qualified pipeline — leads that survived the BDR review. Marketing uses this to allocate budget toward the channels that produce SALs, not the channels that produce raw volume. A channel that costs $200 per lead but produces a 70% SAL rate is more valuable than a channel that costs $80 per lead with a 30% SAL rate.",
    action: "read",
    route: "/#/funnel",
  },
  {
    stepNumber: 5,
    selector: '[data-tour="funnel-bdr-handoff-queue"]',
    title: "Step 5 — BDR handoff queue",
    narration:
      "The handoff queue shows the leads marketing has declared qualified that are currently sitting in the BDR's queue, waiting for review. These are leads marketing claims are ready — but the BDR has not yet accepted or rejected them. If the queue is long, there is a throughput problem: either the BDR does not have enough time, or the leads are not good enough to prioritize. Marketing can see the age of each lead in the queue. A 12-day-old MQL that has not been reviewed is a pipeline hygiene problem marketing and BDR share.",
    coachingBeat:
      "Handoff queue status: 7 leads waiting for BDR review. Oldest lead: 9 days. Average time to BDR review this month: 3.4 days. Two leads are from the highest-converting source (referral) and have been waiting 6 days. These should be surfaced to the BDR today.",
    action: "read",
    route: "/#/funnel",
  },
  {
    stepNumber: 6,
    selector: '[data-tour="funnel-targets-table"]',
    title: "Step 6 — Targets table: quarterly goals vs actuals",
    narration:
      "The targets table shows the quarterly plan versus the current reality: MQL target vs actual, SAL target vs actual, pipeline value target vs actual, closed-won target vs actual. This is the scorecard Marketing owns. If MQL volume is on track but SAL conversion is below target, the system tells Marketing which problem to solve — not more leads, better leads. The quarterly goal comparison is not a report for Bryan K. Short. It is Marketing's operating dashboard, updated in real time.",
    coachingBeat:
      "Q2 targets update: MQL target: 120. Actuals to date: 94 (78% of target, 8 weeks remaining). SAL conversion: 55% vs 68% target. Pipeline value generated: $1.2M vs $1.8M target. The system's recommended action: channel reallocation toward referral and organic, which are converting at 79% SAL rate vs paid channels at 42%.",
    action: "read",
    route: "/#/funnel",
  },
];
